import { useEffect, useState } from 'react';
import type { NearbySpot } from '@cubemeet/ui/components/travel/NearbyMap';
import { hasCoordinates, extractItemCoordinates } from '../utils/coordinateHelpers';
import { parseTimeToMinutes } from '../timeUtils';

export interface UseRouteMapOptions {
  showRouteMap: boolean;
  selectedDay: any;
  supabaseClient: any;
}

export interface UseRouteMapResult {
  routeSpots: NearbySpot[] | null;
  routeSpotsLoading: boolean;
}

/**
 * 地图路线数据管理 Hook
 * 负责：
 * 1. 从 timeline items 提取坐标信息
 * 2. 对于缺少坐标的 items，从数据库异步获取
 * 3. 按时间排序确保地图标记顺序正确
 * 4. 构建统一的 NearbySpot 数据格式
 */
export function useRouteMap({
  showRouteMap,
  selectedDay,
  supabaseClient
}: UseRouteMapOptions): UseRouteMapResult {
  const [routeSpots, setRouteSpots] = useState<NearbySpot[] | null>(null);
  const [routeSpotsLoading, setRouteSpotsLoading] = useState<boolean>(false);

  useEffect(() => {
    // 关闭地图时重置状态
    if (!showRouteMap) {
      setRouteSpots(null);
      setRouteSpotsLoading(false);
      return;
    }

    // 避免重复加载
    if (routeSpotsLoading || routeSpots !== null) return;

    const items = selectedDay?.items || [];
    if (items.length === 0) {
      setRouteSpots([]);
      return;
    }

    // 按时间排序 items，确保地图标记顺序与列表显示一致
    const sortedItems = items.slice().sort((a: any, b: any) => {
      const timeA = parseTimeToMinutes(a?.time);
      const timeB = parseTimeToMinutes(b?.time);
      return timeA - timeB;
    });

    // 扫描需要从数据库获取坐标的 card IDs
    const idsToFetch: string[] = [];
    for (const item of sortedItems) {
      // 如果 item 已有坐标，跳过
      if (hasCoordinates(item)) continue;

      // 提取 cardId
      const cardId = item?.meta?.cardId || item?.card?.id || item?.card_id || item?.cardId;
      if (cardId) {
        const idStr = String(cardId);
        if (!idsToFetch.includes(idStr)) {
          idsToFetch.push(idStr);
        }
      }
    }

    // 如果所有 items 都有坐标或都没有 cardId，直接构建 spots
    if (idsToFetch.length === 0) {
      const initialSpots = buildSpotsFromItems(sortedItems);
      setRouteSpots(initialSpots);
      return;
    }

    // 从数据库异步获取缺失的坐标信息
    fetchCardCoordinates(idsToFetch, sortedItems);
  }, [showRouteMap, selectedDay, supabaseClient, routeSpotsLoading, routeSpots]);

  /**
   * 从 items 构建 NearbySpot 数组
   */
  const buildSpotsFromItems = (items: any[]): NearbySpot[] => {
    return items
      .map((item: any, idx: number) => {
        const latlng = extractItemCoordinates(item);
        if (!latlng) return null;

        const src = item?.payload || item?.custom_payload || item?.inline_payload || item;
        const name = item?.title || src?.title || src?.name || `景点 ${idx + 1}`;
        const imageUrl = src?.imageUrl || src?.image || src?.cover_image || src?.thumbnail || null;
        const address = src?.address || src?.formatted_address || null;

        return {
          id: String(item?.id || idx),
          name,
          lat: latlng[0],
          lng: latlng[1],
          imageUrl,
          address
        } as NearbySpot;
      })
      .filter(Boolean) as NearbySpot[];
  };

  /**
   * 从数据库获取卡片坐标信息
   */
  const fetchCardCoordinates = async (cardIds: string[], sortedItems: any[]) => {
    setRouteSpotsLoading(true);

    try {
      const { data, error } = await supabaseClient
        .from('cards_cards')
        .select('id, title, latitude, longitude, base_payload, cover_image, address')
        .in('id', cardIds as any);

      if (error) {
        console.warn('[useRouteMap] Card fetch error:', error);
        setRouteSpots([]);
        setRouteSpotsLoading(false);
        return;
      }

      if (!Array.isArray(data)) {
        setRouteSpots([]);
        setRouteSpotsLoading(false);
        return;
      }

      // 构建坐标映射表
      const coordMap: Record<string, {
        lat?: number;
        lng?: number;
        title?: string;
        cover_image?: string;
        address?: string;
      }> = {};

      for (const row of data) {
        const id = String(row.id);
        const la = row.latitude ?? (row.base_payload && (row.base_payload.latitude ?? row.base_payload.lat)) ?? null;
        const lo = row.longitude ?? (row.base_payload && (row.base_payload.longitude ?? row.base_payload.lng)) ?? null;
        const lat = la === null || typeof la === 'undefined' ? null : Number(la);
        const lng = lo === null || typeof lo === 'undefined' ? null : Number(lo);

        coordMap[id] = {
          title: row.title,
          cover_image: row.cover_image,
          address: row.address
        };

        if (lat !== null && lng !== null && !Number.isNaN(lat) && !Number.isNaN(lng)) {
          coordMap[id].lat = lat;
          coordMap[id].lng = lng;
        }
      }

      // 构建最终的 spots 数组（保持 sortedItems 的顺序）
      const enrichedSpots: NearbySpot[] = sortedItems
        .map((item: any, idx: number) => {
          const src = item?.payload || item?.custom_payload || item?.inline_payload || item;

          // 1. 优先使用 item 自身的坐标
          const latlng = extractItemCoordinates(item);
          if (latlng && !Number.isNaN(latlng[0]) && !Number.isNaN(latlng[1])) {
            return {
              id: String(item?.id || idx),
              name: item?.title || src?.title || src?.name || `景点 ${idx + 1}`,
              lat: latlng[0],
              lng: latlng[1],
              imageUrl: src?.imageUrl || src?.image || src?.cover_image || src?.thumbnail || null,
              address: src?.address || src?.formatted_address || null
            } as NearbySpot;
          }

          // 2. 回退到从数据库获取的坐标
          const cardId = item?.meta?.cardId || item?.card?.id || item?.card_id || item?.cardId;
          if (cardId && coordMap[String(cardId)]) {
            const m = coordMap[String(cardId)];
            if (m.lat && m.lng) {
              return {
                id: String(item?.id || idx),
                name: item?.title || m.title || `景点 ${idx + 1}`,
                lat: m.lat,
                lng: m.lng,
                imageUrl: m.cover_image || src?.imageUrl || src?.image || src?.cover_image || src?.thumbnail || null,
                address: m.address || src?.address || src?.formatted_address || null
              } as NearbySpot;
            }
          }

          return null;
        })
        .filter(Boolean) as NearbySpot[];

      setRouteSpots(enrichedSpots);
      console.debug('[useRouteMap] Enriched route spots:', enrichedSpots);
    } catch (err) {
      console.error('[useRouteMap] Fetch error:', err);
      setRouteSpots([]);
    } finally {
      setRouteSpotsLoading(false);
    }
  };

  return {
    routeSpots,
    routeSpotsLoading
  };
}
